import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";

const AdminAuthCtx = createContext({
  admin: undefined,       // undefined = loading, null = logged out, {email,id} = logged in
  login: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

export function useAdminAuth() {
  return useContext(AdminAuthCtx);
}

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/me");
      setAdmin(data);
    } catch {
      setAdmin(null);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/admin/login", { email, password });
    setAdmin({ email: data.email, id: data.id });
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/admin/logout");
    } catch { /* ignore */ }
    setAdmin(null);
  }, []);

  return (
    <AdminAuthCtx.Provider value={{ admin, login, logout, refresh }}>
      {children}
    </AdminAuthCtx.Provider>
  );
}

export function RequireAdmin({ children }) {
  const { admin } = useAdminAuth();
  const loc = useLocation();

  if (admin === undefined) {
    return (
      <div data-testid="admin-loading" className="min-h-screen flex items-center justify-center text-sm text-neutral-500">
        Checking session…
      </div>
    );
  }
  if (admin === null) {
    return <Navigate to="/admin/login" replace state={{ from: loc.pathname }} />;
  }
  return children;
}
